import { AnyChart } from '@d3fc/d3fc-chart';
import { YAxisContext } from './yAxisContext';
import { select, BaseType } from 'd3-selection';
import { AnySelection } from '@d3fc/d3fc-series';
import { WrappedValueLine } from '../../../utils/text/wrapText';
import { getLineHeight } from 'utils/types/Style';
import { YAxisMouseOver } from './YAxisMouseOver';

interface WrappedLineValueWithLineCount extends WrappedValueLine {
    lineCount: number;
}

export function renderYAxis(
    chart: AnyChart,
    node: HTMLDivElement,
    context: YAxisContext,
    yAxisMouseOver: YAxisMouseOver
) {
    const width = `${context.width}px`;
    const lineHeight = getLineHeight(context.style);

    select(node).select('.y-axis').style('width', width);

    const getWrapped = (text: string) => {
        const index = context.values.indexOf(text);
        return context.wrappedValues[index];
    };

    chart.yDecorate((selection: AnySelection<BaseType, string>) => {
        // This will be hidden by CSS
        selection.enter().select('text').remove();

        // We add lines for the wrapped text
        const lines: AnySelection<BaseType, WrappedLineValueWithLineCount> =
            selection
                .enter()
                .selectAll('.line')
                .data((text: string) => {
                    const wrapped = getWrapped(text);
                    return wrapped.lines.map<WrappedLineValueWithLineCount>(
                        (wrappedValueLine) => ({
                            ...wrappedValueLine,
                            lineCount: wrapped.lines.length,
                        })
                    );
                });

        lines
            .enter()
            .append('text')
            .attr('class', 'line')
            .text((t: WrappedValueLine) => t.value)
            .attr(
                'transform',
                (t: WrappedLineValueWithLineCount, index: number) => {
                    const start =
                        ((t.lineCount * lineHeight) / 2 - lineHeight / 2) * -1 +
                        2;
                    const yOffset = start + lineHeight * index;
                    const tickWidth = t.width;
                    const xOffset = context.width - tickWidth - context.padding;
                    return `translate(${xOffset},${yOffset})`;
                }
            );

        lines.exit().remove();

        /*
         * Use a rectangle for the tooltip mouseover that covers all lines to the max line width
         */
        selection
            .enter()
            .append('rect')
            .attr('class', 'y-axis-mouseover')
            .attr('x', 0)
            // Width of widest line
            .attr('width', context.width)
            // Height of all lines
            .attr('height', (text: string) => {
                const wrapped = getWrapped(text);
                return wrapped.lines.length * lineHeight;
            })
            .attr('y', (text: string) => {
                const wrapped = getWrapped(text);
                return ((wrapped.lines.length * lineHeight) / 2) * -1 - 2;
            })
            .on('mouseenter', (event, text: string) => {
                const wrapped = getWrapped(text);
                if (wrapped.isTruncated) {
                    yAxisMouseOver.publish({
                        text,
                        x: context.width + 10,
                        y: event.offsetY,
                    });
                }
            })
            .on('mouseleave', (event) => {
                yAxisMouseOver.publish({ x: event.offsetX, y: event.offsetY });
            });
    });
}
